import React from 'react';
import { Upload } from 'lucide-react';
import { toast } from 'react-toastify';


const FileUploadSection = ({
    selectedFiles,
    setSelectedFiles,
    errors,
    setErrors,
    isProcessing
}) => {
    const handleFileSelect = (event) => {
        const newFiles = Array.from(event.target.files);
        
        // Validate file types
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        const invalidFiles = newFiles.filter(file => !allowedTypes.includes(file.type));
        
        if (invalidFiles.length > 0) {
            toast.error(`Invalid file types detected. Please upload only PDF, DOC, or DOCX files.`, {
                position: "top-right",
                autoClose: 5000,
            });
            return;
        }
        
        // Validate file size (10MB limit per file)
        const maxSize = 10 * 1024 * 1024; // 10MB
        const oversizedFiles = newFiles.filter(file => file.size > maxSize);
        
        if (oversizedFiles.length > 0) {
            toast.error(`Some files are too large. Maximum file size is 10MB.`, {
                position: "top-right",
                autoClose: 5000,
            });
            return;
        }
        
        setSelectedFiles(prev => [...prev, ...newFiles]);
        event.target.value = '';
        
        // Clear file errors when files are selected
        if (errors.files && newFiles.length > 0) {
            setErrors(prev => ({ ...prev, files: undefined }));
        }
    };

    const removeFile = (indexToRemove) => {
        setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const clearAllFiles = () => {
        setSelectedFiles([]);
    };

    return (
        <div className="mb-8">
            <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx"
                onChange={handleFileSelect}
                className="hidden"
                id="resume-upload"
                disabled={isProcessing}
            />
            <label
                htmlFor="resume-upload"
                className={`block border-2 rounded-xl p-4 transition-colors ${
                    isProcessing 
                        ? 'cursor-not-allowed opacity-50' 
                        : 'hover:border-gray-400 cursor-pointer'
                } ${errors.files ? 'border-red-300' : 'border-[#DADADA]'}`}
            >
                <div className="flex items-center justify-between">
                    <div className="flex text-center">
                        <div className="px-4 p-2 text-lg text-gray-500 mb-2 bg-[#F5F6F6] rounded-xl">
                            Upload Resumes
                        </div>
                        <div className="text-gray-500 px-2 p-2">
                            {selectedFiles.length > 0
                                ? `${selectedFiles.length} file(s) selected`
                                : 'No files chosen'
                            }
                        </div>
                    </div>
                    <Upload className="w-8 h-8 text-gray-400 hover:text-gray-600 mr-8" />
                </div>
            </label>
            
            {errors.files && (
                <div className="text-red-500 text-sm mt-1 text-left">
                    {errors.files}
                </div>
            )}

            {/* Selected Files */}
            {selectedFiles.length > 0 && (
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 mb-3">Selected Files:</div>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                        {selectedFiles.map((file, index) => (
                            <div key={index} className="flex justify-between items-center bg-white p-2 rounded border">
                                <span className="text-sm text-gray-600 truncate flex-1">{file.name}</span>
                                <span className="text-xs text-gray-400 mx-2">
                                    {(file.size / (1024 * 1024)).toFixed(1)}MB
                                </span>
                                <button
                                    onClick={() => removeFile(index)}
                                    className="text-red-500 hover:text-red-700 ml-2 text-sm"
                                    type="button"
                                    disabled={isProcessing}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={clearAllFiles}
                        className="mt-3 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        type="button"
                        disabled={isProcessing}
                    >
                        Clear all files
                    </button>
                </div>
            )}
        </div>
    );
};

export default FileUploadSection;